
import React, { useState } from 'react';
import { Database, PlusCircle, InfoIcon, Edit, Trash, Power } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { useDatabasesData } from './hooks/useDatabasesData';
import AddDatabaseDialog from './dialogs/AddDatabaseDialog';
import EditDatabaseDialog from './dialogs/EditDatabaseDialog';
import DeleteDatabaseDialog from './dialogs/DeleteDatabaseDialog';

interface DatabasesListProps {
  editing: boolean;
}

const DatabasesList: React.FC<DatabasesListProps> = ({ editing }) => {
  const { databases, addDatabase, updateDatabase, removeDatabase } = useDatabasesData();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedDatabase, setSelectedDatabase] = useState<any>(null);

  const handleEdit = (database: any) => {
    setSelectedDatabase(database);
    setIsEditDialogOpen(true);
  };

  const handleDelete = (database: any) => {
    setSelectedDatabase(database);
    setIsDeleteDialogOpen(true);
  };

  const statusColors: Record<string, string> = {
    connected: 'bg-green-500',
    disconnected: 'bg-red-500',
    pending: 'bg-yellow-500'
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Database Connections</h3>
        {editing && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setIsAddDialogOpen(true)}
            className="flex items-center gap-1"
          >
            <PlusCircle className="h-4 w-4" />
            Add Database
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {databases.map(db => (
          <HoverCard key={db.id}>
            <HoverCardTrigger asChild>
              <Card className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="p-4 border-b">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2">
                        <Database className="h-4 w-4 text-primary" />
                        <h4 className="font-medium">{db.name}</h4>
                      </div>
                      <Badge 
                        variant="outline" 
                        className={`text-white ${statusColors[db.status]}`}
                      >
                        {db.status}
                      </Badge>
                    </div>
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Type:</span>
                      <span>{db.type}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Host:</span>
                      <span>{db.host}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Database:</span>
                      <span>{db.database}</span>
                    </div>
                  </div>
                  {editing && (
                    <div className="flex border-t divide-x">
                      <Button 
                        variant="ghost" 
                        className="flex-1 rounded-none h-10"
                        onClick={() => handleEdit(db)}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button 
                        variant="ghost" 
                        className="flex-1 rounded-none h-10 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                        onClick={() => handleDelete(db)}
                      >
                        <Trash className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </HoverCardTrigger>
            <HoverCardContent className="w-80">
              <div className="space-y-2">
                <h4 className="font-semibold">{db.name} Details</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-muted-foreground">Connection Type:</div>
                  <div>{db.type}</div>
                  
                  <div className="text-muted-foreground">Host:</div>
                  <div>{db.host}</div>
                  
                  <div className="text-muted-foreground">Port:</div>
                  <div>{db.port}</div>
                  
                  <div className="text-muted-foreground">Database:</div>
                  <div>{db.database}</div>
                  
                  <div className="text-muted-foreground">Username:</div>
                  <div>{db.username}</div>
                  
                  <div className="text-muted-foreground">Status:</div>
                  <div className="flex items-center">
                    <span className={`h-2 w-2 rounded-full mr-2 ${statusColors[db.status]}`}></span>
                    {db.status}
                  </div>
                </div>
                {editing && db.status !== 'connected' && (
                  <Button size="sm" className="w-full mt-2">
                    <Power className="h-4 w-4 mr-2" /> 
                    Test Connection
                  </Button>
                )}
              </div>
            </HoverCardContent>
          </HoverCard>
        ))}

        {databases.length === 0 && (
          <Card className="col-span-full p-6 text-center">
            <div className="flex flex-col items-center justify-center space-y-2">
              <Database className="h-8 w-8 text-muted-foreground" />
              <h3 className="text-lg font-medium">No Databases Connected</h3>
              <p className="text-muted-foreground">
                Add a database connection to integrate with external data sources.
              </p>
              {editing && (
                <Button 
                  onClick={() => setIsAddDialogOpen(true)}
                  className="mt-2"
                >
                  Add Database
                </Button>
              )}
            </div>
          </Card>
        )}
      </div>

      <AddDatabaseDialog 
        open={isAddDialogOpen} 
        onOpenChange={setIsAddDialogOpen} 
        onAdd={addDatabase} 
      />
      
      {selectedDatabase && (
        <>
          <EditDatabaseDialog 
            open={isEditDialogOpen} 
            onOpenChange={setIsEditDialogOpen} 
            database={selectedDatabase}
            onUpdate={updateDatabase}
          />
          
          <DeleteDatabaseDialog 
            open={isDeleteDialogOpen} 
            onOpenChange={setIsDeleteDialogOpen} 
            database={selectedDatabase}
            onDelete={removeDatabase}
          />
        </>
      )}
    </div>
  );
};

export default DatabasesList;
